/** Session detail slide-over — 2026-09-17.
 *
 * The operator's own words: a session list/calendar chip only ever
 * showed a status pill, a duration, and an operator email — "the
 * session id from which I can never guess who it's with." And
 * clicking a session ANYWHERE (list, calendar) jumped straight into
 * the live call room, even for a merely-scheduled or already-closed
 * session, instead of showing who it's with, when, and with whom.
 *
 * This drawer is the one place every entry point (SessionsTable row,
 * Calendar chip/agenda row) now opens: who the beneficiary is, who
 * the operator is, when it's scheduled/happened, and only THEN a
 * button to actually join the call / view the recording / audit /
 * participants — mirroring BeneficiaryDetailDrawer's "summary row,
 * everything else behind one click" shape.
 *
 * Self-contained: takes just a `sessionId` and fetches the Session
 * itself (same `["session", id]` query key SessionPage uses, so
 * opening "Ouvrir" afterwards hits a warm cache).
 */
import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { SiteCards } from "@/components/SiteCards";
import { worksitesOf } from "@/components/worksites";
import { ApiPaths, api } from "@/lib/api";
import { formatDateTime, formatDuration } from "@/lib/format";
import { displayPhone } from "@/lib/phone";
import type { CampaignMembership, Contact, Session } from "@/lib/types";
import {
  MembershipFieldsCard,
  ScheduleLeadModal,
  resolveFieldSchema,
} from "@/modules/campaigns";

import { StatusPill } from "@/components/StatusPill";

import { ExternalAudioUpload } from "./ExternalAudioUpload";

/** A session past the point of being moved. Mirrors SessionPage's own
 *  private copy; kept local rather than exported from a page module. */
function isTerminal(status: Session["status"]): boolean {
  return ["closed", "expired", "cancelled"].includes(status);
}

export function SessionDetailDrawer({
  sessionId,
  onClose,
}: {
  sessionId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rescheduling, setRescheduling] = useState(false);

  const q = useQuery({
    queryKey: ["session", sessionId],
    queryFn: async () =>
      (await api.get<Session>(ApiPaths.session(sessionId))).data,
  });
  const session = q.data;

  // 2026-09-18 — a click here used to stop at name + a link to go dig up
  // the beneficiary elsewhere. Pull the full Contact (addresses included —
  // the serializer inlines them) so who/where/how-to-reach-them is all
  // right here, not a second click away.
  const beneficiary = useQuery({
    queryKey: ["contact", session?.beneficiary_contact_id],
    queryFn: async () =>
      (
        await api.get<Contact>(
          ApiPaths.contact(session!.beneficiary_contact_id!),
        )
      ).data,
    enabled: !!session?.beneficiary_contact_id,
  });

  // Backlog 2026-09-18 — "complete detail" means the custom fields too,
  // not just the fixed identity/address fields above. Contact-scoped
  // fields (DNI/CIF/SIRET…) come off the beneficiary's own record;
  // membership-scoped ones (this campaign's own extra questions) come
  // off THIS session's specific membership — a beneficiary in several
  // campaigns has a different set/values per campaign.
  const membership = useQuery({
    queryKey: ["campaign-lead", session?.campaign, session?.membership],
    queryFn: async () =>
      (
        await api.get<CampaignMembership>(
          ApiPaths.campaignLead(session!.campaign!, session!.membership!),
        )
      ).data,
    enabled: !!session?.campaign && !!session?.membership,
  });

  // Backlog 2026-09-21 — this used to resolve WITHOUT `campaign`, so any
  // campaign-scoped contact custom field (which is most of them — the
  // org-wide/system catalog is the exception, not the rule) silently
  // never showed here even when it had a real value. A field that's
  // visible on the campaign's own beneficiary-list drawer could be
  // completely invisible on this one, for the exact same beneficiary.
  const contactFieldSchema = useQuery({
    queryKey: [
      "fields-schema",
      "contact",
      beneficiary.data?.country ?? "",
      session?.campaign ?? "",
    ],
    queryFn: () =>
      resolveFieldSchema({
        applies_to: "contact",
        country: beneficiary.data?.country ?? "",
        campaign: session?.campaign ?? undefined,
      }),
    enabled: !!beneficiary.data,
  });
  const filledContactFields = useMemo(() => {
    const rows = contactFieldSchema.data ?? [];
    const values = beneficiary.data?.custom_fields_jsonb ?? {};
    return rows.filter((fd) => (values[fd.key] ?? "") !== "");
  }, [contactFieldSchema.data, beneficiary.data]);

  const membershipFieldSchema = useQuery({
    queryKey: ["fields-schema", "membership", session?.campaign ?? ""],
    queryFn: () =>
      resolveFieldSchema({
        applies_to: "membership",
        campaign: session?.campaign ?? undefined,
      }),
    enabled: !!session?.campaign,
  });

  // Backlog 2026-09-21 — a safety net so a custom-field value can never
  // again sit in the database invisible to every screen just because
  // its key doesn't match any currently-resolved FieldDefinition (wrong
  // campaign scope, an import that mapped it under an unexpected key, a
  // field definition that was never created). Anything present in
  // custom_fields_jsonb that ISN'T covered by the schema above still
  // gets shown, raw key and all, rather than silently disappearing.
  const unmappedContactFields = useMemo(() => {
    const known = new Set((contactFieldSchema.data ?? []).map((fd) => fd.key));
    const values = beneficiary.data?.custom_fields_jsonb ?? {};
    return Object.entries(values).filter(
      ([key, value]) => !known.has(key) && value !== "" && value != null,
    );
  }, [contactFieldSchema.data, beneficiary.data]);
  const unmappedMembershipFields = useMemo(() => {
    const known = new Set(
      (membershipFieldSchema.data ?? []).map((fd) => fd.key),
    );
    const values = membership.data?.custom_fields_jsonb ?? {};
    return Object.entries(values).filter(
      ([key, value]) => !known.has(key) && value !== "" && value != null,
    );
  }, [membershipFieldSchema.data, membership.data]);

  // A closed session's detail lives on the media page — same rule the
  // sessions-list row action used before this drawer existed.
  const openHref = session
    ? session.status === "closed"
      ? `/sessions/${session.id}/media`
      : `/sessions/${session.id}`
    : "#";
  const openLabel =
    session?.status === "closed"
      ? t("sessionDetail.actionViewClosed", "View session")
      : session?.status === "created" && session.scheduled_for
        ? t("sessionDetail.actionJoinScheduled", "Join / start call")
        : t("sessionDetail.actionJoin", "Join call");

  const heading =
    session?.beneficiary_name ||
    session?.title ||
    session?.campaign_name ||
    t("sessionDetail.standaloneTitle", "Standalone session");

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("sessionDetail.title", "Session details")}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l border-nb-border bg-nb-surface shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-nb-border px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-semibold text-nb-text">
                {heading}
              </h2>
              {session && <StatusPill status={session.status} />}
              {/* The appointment time, in the drawer she opens FROM the
                  calendar. It was already here — ~140 lines down in the
                  details list, past the quick actions and the whole
                  beneficiary block. */}
              {session?.scheduled_for && (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-nb-mint/40 bg-nb-mint/10 px-2 py-0.5 text-[11px] font-medium text-nb-mint"
                  data-testid="session-scheduled-chip"
                >
                  {t("sessionDetail.scheduledFor")}:{" "}
                  {formatDateTime(session.scheduled_for)}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="btn-ghost shrink-0 text-xs"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
          >
            ✕
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {q.isLoading && (
            <p className="text-sm text-nb-text-2">{t("common.loading")}</p>
          )}

          {session && (
            <>
              {/* Quick actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary text-xs"
                  onClick={() => navigate(openHref)}
                  data-testid="session-detail-open"
                >
                  {openLabel}
                </button>
                {/* Not just platform recordings: a session whose call
                    never connected can still hold uploaded phone audio,
                    and stays un-closed — so offer media whenever there
                    is any. */}
                {(session.has_recording ||
                  (session.evidence_count ?? 0) > 0) && (
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={() => navigate(`/sessions/${session.id}/media`)}
                  >
                    {t("sessionsList.actionMedia")}
                  </button>
                )}
                {/* Backlog 2026-09-22 — the inspection Q&A can always be
                    filled in after the fact (neither the API nor the
                    panel gates on session status), but the only route to
                    it was an unlabelled tab on the media page. Surfaced
                    here so a closed session's questions can be answered
                    from the session list / calendar without hunting.
                    Campaign-linked sessions only — a standalone session
                    has no question set. */}
                {session.campaign && (
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={() =>
                      navigate(`/sessions/${session.id}/media?tab=qa`)
                    }
                  >
                    {t("sessionsList.actionQa", "Q&A")}
                  </button>
                )}
                {/* Reported 2026-09-25: the operator watches her daily
                    calendar, opens the appointment at its scheduled
                    time, the beneficiary phones to say they can't make
                    it — and the only Reschedule in the product was in
                    the campaign leads table. Offered here for a
                    campaign session that hasn't happened yet; there is
                    nothing to move about a closed one. */}
                {session.campaign &&
                  session.membership &&
                  !isTerminal(session.status) && (
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      onClick={() => setRescheduling(true)}
                      data-testid="session-detail-reschedule"
                    >
                      {session.scheduled_for
                        ? t("leads.rescheduleButton")
                        : t("leads.scheduleButton")}
                    </button>
                  )}
                {/* Reported 2026-09-25: when the connection won't hold,
                    she runs the interview by phone and needs somewhere
                    to put the recording. Filed as external_audio, never
                    as a platform recording. */}
                <ExternalAudioUpload sessionId={session.id} />
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => navigate(`/sessions/${session.id}/audit`)}
                >
                  {t("sessionsList.actionAudit")}
                </button>
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  onClick={() => navigate(`/sessions/${session.id}/participants`)}
                >
                  {t("sessionsList.actionParticipants")}
                </button>
              </div>

              {/* Who / when */}
              <section className="space-y-2 rounded-lg border border-nb-border bg-nb-bg/60 p-3">
                <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-nb-text-3">
                      {t("sessionDetail.beneficiary", "Beneficiary")}
                    </dt>
                    <dd className="text-nb-text">
                      {session.beneficiary_name ? (
                        session.beneficiary_contact_id ? (
                          <Link
                            to={`/contacts/${session.beneficiary_contact_id}`}
                            className="text-nb-mint hover:underline"
                          >
                            {session.beneficiary_name}
                          </Link>
                        ) : (
                          session.beneficiary_name
                        )
                      ) : (
                        <span className="text-nb-text-3">
                          {t("sessionDetail.noBeneficiary", "None (standalone)")}
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-nb-text-3">
                      {t("sessionDetail.operator", "Operator")}
                    </dt>
                    <dd className="text-nb-text">
                      {session.operator_email || "—"}
                    </dd>
                  </div>
                  {session.campaign_name && (
                    <div>
                      <dt className="text-xs text-nb-text-3">
                        {t("sessionDetail.campaign", "Campaign")}
                      </dt>
                      <dd className="text-nb-text">{session.campaign_name}</dd>
                    </div>
                  )}
                  {session.scheduled_for && (
                    <div>
                      <dt className="text-xs text-nb-text-3">
                        {t("sessionDetail.scheduledFor", "Scheduled for")}
                      </dt>
                      <dd className="text-nb-text">
                        {formatDateTime(session.scheduled_for)}
                      </dd>
                    </div>
                  )}
                  {session.started_at && (
                    <div>
                      <dt className="text-xs text-nb-text-3">
                        {t("sessionDetail.startedAt", "Started")}
                      </dt>
                      <dd className="text-nb-text">
                        {formatDateTime(session.started_at)}
                      </dd>
                    </div>
                  )}
                  {session.ended_at && (
                    <div>
                      <dt className="text-xs text-nb-text-3">
                        {t("sessionDetail.endedAt", "Ended")}
                      </dt>
                      <dd className="text-nb-text">
                        {formatDateTime(session.ended_at)}
                      </dd>
                    </div>
                  )}
                  {session.duration_seconds != null && (
                    <div>
                      <dt className="text-xs text-nb-text-3">
                        {t("sessionsList.colDuration")}
                      </dt>
                      <dd className="font-mono text-nb-text">
                        {formatDuration(session.duration_seconds)}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-xs text-nb-text-3">
                      {t("sessionsList.colParticipants")}
                    </dt>
                    <dd className="font-mono text-nb-text">
                      {session.participant_count}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-nb-text-3">
                      {t("sessionsList.colEvidence")}
                    </dt>
                    <dd className="font-mono text-nb-text">
                      {session.evidence_count}
                    </dd>
                  </div>
                </dl>
              </section>

              {session.notes && (
                <section className="space-y-1">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
                    {t("sessionDetail.notes", "Notes")}
                  </h3>
                  <p className="whitespace-pre-wrap text-sm text-nb-text">
                    {session.notes}
                  </p>
                </section>
              )}

              {session.beneficiary_contact_id && (
                <section className="space-y-2 rounded-lg border border-nb-border bg-nb-bg/60 p-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
                      {t("sessionDetail.beneficiaryDetails", "Beneficiary details")}
                    </h3>
                    <Link
                      to={`/contacts/${session.beneficiary_contact_id}`}
                      className="text-xs text-nb-mint hover:underline"
                    >
                      {t("sessionDetail.viewFullProfile", "Full profile →")}
                    </Link>
                  </div>

                  {beneficiary.isLoading && (
                    <p className="text-sm text-nb-text-2">{t("common.loading")}</p>
                  )}

                  {beneficiary.data && (
                    <>
                      <div className="flex flex-wrap gap-1.5">
                        {beneficiary.data.is_invalid && (
                          <span className="rounded-full bg-cvp-danger/15 px-2 py-0.5 text-xs text-cvp-danger">
                            {t("contacts.flagInvalid")}
                          </span>
                        )}
                        {beneficiary.data.do_not_call && (
                          <span className="rounded-full bg-cvp-warn/15 px-2 py-0.5 text-xs text-cvp-warn">
                            {t("contacts.flagDoNotCallLabel")}
                          </span>
                        )}
                      </div>

                      <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
                        {beneficiary.data.email && (
                          <div>
                            <dt className="text-xs text-nb-text-3">
                              {t("sessionDetail.email", "Email")}
                            </dt>
                            <dd className="text-nb-text">
                              <a
                                href={`mailto:${beneficiary.data.email}`}
                                className="hover:underline"
                              >
                                {beneficiary.data.email}
                              </a>
                            </dd>
                          </div>
                        )}
                        {beneficiary.data.phone && (
                          <div>
                            <dt className="text-xs text-nb-text-3">
                              {t("sessionDetail.phone", "Phone")}
                            </dt>
                            <dd className="text-nb-text">
                              <a
                                href={`tel:${beneficiary.data.phone}`}
                                className="hover:underline"
                              >
                                {displayPhone(beneficiary.data.phone)}
                              </a>
                            </dd>
                          </div>
                        )}
                        {beneficiary.data.external_ref && (
                          <div>
                            <dt className="text-xs text-nb-text-3">
                              {t("sessionDetail.externalRef", "External ref.")}
                            </dt>
                            <dd className="font-mono text-nb-text">
                              {beneficiary.data.external_ref}
                            </dd>
                          </div>
                        )}
                        {beneficiary.data.is_business && (
                          <>
                            {beneficiary.data.legal_name && (
                              <div>
                                <dt className="text-xs text-nb-text-3">
                                  {t("sessionDetail.legalName", "Legal name")}
                                </dt>
                                <dd className="text-nb-text">
                                  {beneficiary.data.legal_name}
                                </dd>
                              </div>
                            )}
                            {beneficiary.data.registration_number && (
                              <div>
                                <dt className="text-xs text-nb-text-3">
                                  {t(
                                    "sessionDetail.registrationNumber",
                                    "Registration no.",
                                  )}
                                </dt>
                                <dd className="text-nb-text">
                                  {beneficiary.data.registration_number}
                                </dd>
                              </div>
                            )}
                            {beneficiary.data.company_contact_name && (
                              <div>
                                <dt className="text-xs text-nb-text-3">
                                  {t(
                                    "sessionDetail.companyContact",
                                    "Company contact",
                                  )}
                                </dt>
                                <dd className="text-nb-text">
                                  {beneficiary.data.company_contact_name}
                                  {beneficiary.data.company_contact_phone && (
                                    <>
                                      {" · "}
                                      {displayPhone(
                                        beneficiary.data.company_contact_phone,
                                      )}
                                    </>
                                  )}
                                </dd>
                              </div>
                            )}
                          </>
                        )}
                      </dl>

                      {beneficiary.data.addresses.length > 0 && (
                        <div className="space-y-1">
                          <dt className="text-xs text-nb-text-3">
                            {t("sessionDetail.addresses", {
                              count: worksitesOf(beneficiary.data.addresses)
                                .length,
                            })}
                          </dt>
                          {/* Numbered 2026-09-23 — a multi-site
                              beneficiary showed near-identical lines
                              differing only in a parcel reference
                              buried mid-string. Same numbering on every
                              surface so "site 2" means one thing. */}
                          <SiteCards
                            addresses={worksitesOf(beneficiary.data.addresses)}
                            campaign={session?.campaign ?? undefined}
                          />
                        </div>
                      )}

                      {filledContactFields.length > 0 && (
                        <div className="space-y-1">
                          <dt className="text-xs text-nb-text-3">
                            {t("sessionDetail.customFields", "Custom fields")}
                          </dt>
                          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-sm">
                            {filledContactFields.map((fd) => (
                              <Fragment key={fd.id}>
                                <dt className="text-nb-text-3">{fd.label}</dt>
                                <dd className="text-nb-text">
                                  {String(
                                    beneficiary.data!.custom_fields_jsonb[
                                      fd.key
                                    ] ?? "",
                                  )}
                                </dd>
                              </Fragment>
                            ))}
                          </dl>
                        </div>
                      )}

                      {unmappedContactFields.length > 0 && (
                        <div className="space-y-1">
                          <dt className="text-xs text-nb-text-3">
                            {t(
                              "sessionDetail.otherDataOnFile",
                              "Other data on file",
                            )}
                          </dt>
                          <p className="text-[11px] text-nb-text-3">
                            {t(
                              "sessionDetail.otherDataOnFileHint",
                              "Values stored under a key that doesn't match a defined custom field for this campaign — likely from an import mapping. Shown as-is rather than hidden.",
                            )}
                          </p>
                          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-sm">
                            {unmappedContactFields.map(([key, value]) => (
                              <Fragment key={key}>
                                <dt className="font-mono text-xs text-nb-text-3">
                                  {key}
                                </dt>
                                <dd className="text-nb-text">{String(value)}</dd>
                              </Fragment>
                            ))}
                          </dl>
                        </div>
                      )}

                      {membership.data && (
                        <div className="space-y-2 border-t border-nb-border/60 pt-2">
                          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
                            {(membership.data.project_category !== "generic" ||
                              membership.data.project_code) && (
                              <div>
                                <dt className="text-xs text-nb-text-3">
                                  {t("sessionDetail.project", "Project")}
                                </dt>
                                <dd className="text-nb-text">
                                  {membership.data.project_category !==
                                    "generic" && (
                                    <span className="mr-1 rounded bg-nb-surface px-1.5 py-0.5 font-mono text-[10px] uppercase text-nb-text-2">
                                      {membership.data.project_category}
                                    </span>
                                  )}
                                  {membership.data.project_code}
                                </dd>
                              </div>
                            )}
                            {membership.data.worksite_address_detail && (
                              <div>
                                <dt className="text-xs text-nb-text-3">
                                  {t("sessionDetail.worksite", "Worksite")}
                                </dt>
                                <dd className="text-nb-text">
                                  {membership.data.worksite_address_detail.formatted}
                                </dd>
                              </div>
                            )}
                            {membership.data.outcome_reason && (
                              <div>
                                <dt className="text-xs text-nb-text-3">
                                  {t(
                                    "sessionDetail.outcomeReason",
                                    "Outcome reason",
                                  )}
                                </dt>
                                <dd className="text-nb-text">
                                  {membership.data.outcome_reason}
                                </dd>
                              </div>
                            )}
                          </dl>
                          {membership.data.notes_intake && (
                            <div>
                              <dt className="text-xs text-nb-text-3">
                                {t("sessionDetail.intakeNotes", "Intake notes")}
                              </dt>
                              <dd className="whitespace-pre-wrap text-sm text-nb-text">
                                {membership.data.notes_intake}
                              </dd>
                            </div>
                          )}
                          <div>
                            <dt className="mb-1 text-xs text-nb-text-3">
                              {t(
                                "sessionDetail.campaignFields",
                                "This campaign's fields",
                              )}
                            </dt>
                            <MembershipFieldsCard
                              campaign={membership.data.campaign}
                              membershipId={membership.data.id}
                              values={membership.data.custom_fields_jsonb}
                              canEdit={false}
                            />
                          </div>
                          {unmappedMembershipFields.length > 0 && (
                            <div className="space-y-1">
                              <dt className="text-xs text-nb-text-3">
                                {t(
                                  "sessionDetail.otherDataOnFile",
                                  "Other data on file",
                                )}
                              </dt>
                              <p className="text-[11px] text-nb-text-3">
                                {t(
                                  "sessionDetail.otherDataOnFileHint",
                                  "Values stored under a key that doesn't match a defined custom field for this campaign — likely from an import mapping. Shown as-is rather than hidden.",
                                )}
                              </p>
                              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-sm">
                                {unmappedMembershipFields.map(([key, value]) => (
                                  <Fragment key={key}>
                                    <dt className="font-mono text-xs text-nb-text-3">
                                      {key}
                                    </dt>
                                    <dd className="text-nb-text">
                                      {String(value)}
                                    </dd>
                                  </Fragment>
                                ))}
                              </dl>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </div>
      {/* Reuses the campaign-leads modal rather than a second
          implementation: rescheduling can force the backend to close an
          already-live session and open a fresh one, and the operator has
          to be told a new room was created. A reimplementation here
          would have missed that. */}
      {rescheduling && session?.campaign && membership.data && (
        <ScheduleLeadModal
          lead={membership.data}
          campaignId={session.campaign}
          onClose={() => {
            setRescheduling(false);
            void q.refetch();
          }}
        />
      )}
    </>
  );
}
